import { Controller } from "@hotwired/stimulus"
import { anchorMenu } from "../lib/hudini/floating_menu.js"

// The composer's @-mention: typing `@query` searches records the user can link
// as chat context and inserts the pick as an inline, non-editable mention
// (matching InlineMentionComponent markup). Mentions carry record data
// attributes; the `hudini` controller reads them on send and the server stages
// the records as context.
//
// While the menu is open, the typed `@query` is wrapped in a pending
// `inline-mention--query` pill so it matches the design-system mention chrome
// before a record is chosen. Escape / leaving the token unwraps it; a pick
// replaces the pill with the final mention.
//
// An empty `@` offers the current page's record (and its company) as one-click
// picks without typing a search. The docked panel outlives navigation, so the
// `suggestions` value baked into its HTML only describes the page it was
// rendered on; the rows are re-resolved from the header AI link — which every
// Turbo visit re-renders with the page's record — each time the menu opens
// (fetched from `suggestionsUrl` and cached per record).
//
// The dropdown is anchored to the composer (see lib/hudini/floating_menu.js), so
// it opens above or below depending on the space available.
export default class extends Controller {
  static targets = ["prompt", "menu"]
  static values = {
    url: String,
    suggestionsUrl: String,
    suggestions: { type: Array, default: [] },
  }

  static DEBOUNCE_MS = 200
  static MAX_QUERY_LENGTH = 60
  static QUERY_SELECTOR = ".inline-mention--query"
  // The header entry point, re-rendered by every Turbo visit with the current
  // page's record_type/record_id (the same link the dock borrows them from).
  static HEADER_TRIGGER = "#ai-shortcut"

  connect() {
    this.menuOpen = false
    this.atInfo = null
    this.results = []
    this.activeIndex = 0
    this.cleanupFloating = null
    this.fetchAbort = null
    this.debounceTimer = null
    this.pageSuggestionRows = []
    this.pageSuggestionKey = null
    this.suggestionCache = new Map()
    this.seedSuggestionCache()
    this.boundOutsideClick = this.closeOnOutsideClick.bind(this)
    document.addEventListener("click", this.boundOutsideClick)
  }

  // The server-rendered rows are the current page's at panel load, so they
  // pre-warm the cache for the common case: opening the menu without having
  // navigated. Their first row is always the page record itself.
  seedSuggestionCache() {
    const seeded = Array.isArray(this.suggestionsValue) ? this.suggestionsValue : []
    if (seeded.length === 0) return

    const page = seeded[0]
    if (!page.record_type || !page.record_id) return

    this.suggestionCache.set(`${page.record_type}:${page.record_id}`, seeded)
  }

  disconnect() {
    document.removeEventListener("click", this.boundOutsideClick)
    this.closeMenu()
  }

  get editor() {
    return this.hasPromptTarget ? this.promptTarget : null
  }

  // --- @ detection -------------------------------------------------------------

  // Opens the menu while the caret sits after an `@query` token (start of the
  // message or after whitespace). Queries may contain spaces ("@how to"), so
  // the match runs to the caret and is only closed by Escape, a pick, or
  // deleting the `@`. The active token is wrapped in a pending mention pill.
  detectAt() {
    const info = this.caretAtInfo()
    if (info) {
      this.atInfo = this.ensureQueryMention(info)
      this.openMenu()
      this.search(this.atInfo.query)
    } else if (this.menuOpen) {
      this.closeMenu()
    }
  }

  caretAtInfo() {
    const sel = window.getSelection()
    if (!sel || sel.rangeCount === 0 || !this.editor) return null

    const range = sel.getRangeAt(0)
    if (!range.collapsed) return null

    // Already typing inside a pending query pill.
    const queryEl = this.queryMentionFromNode(range.endContainer)
    if (queryEl && this.editor.contains(queryEl)) {
      const text = queryEl.textContent
      if (!text.startsWith("@")) return null

      const query = text.slice(1)
      if (query.length > this.constructor.MAX_QUERY_LENGTH) return null

      return { queryEl, query }
    }

    const node = range.endContainer
    if (node.nodeType !== Node.TEXT_NODE || !this.editor.contains(node)) return null

    const before = node.textContent.slice(0, range.endOffset)
    const match = before.match(/(?:^|\s)@([^@]*)$/)
    if (!match) return null
    if (match[1].length > this.constructor.MAX_QUERY_LENGTH) return null

    return {
      node,
      start: range.endOffset - match[1].length - 1,
      end: range.endOffset,
      query: match[1],
    }
  }

  queryMentionFromNode(node) {
    const el = node?.nodeType === Node.ELEMENT_NODE ? node : node?.parentElement
    return el?.closest?.(this.constructor.QUERY_SELECTOR) || null
  }

  // Wraps a plain-text `@query` in a pending inline-mention pill. If the caret
  // is already inside one, returns that info unchanged.
  ensureQueryMention(info) {
    if (info.queryEl) return info
    if (!info.node || info.start == null) return info

    const sel = window.getSelection()
    const end = info.end ?? (sel.rangeCount ? sel.getRangeAt(0).endOffset : info.start + info.query.length + 1)

    const range = document.createRange()
    range.setStart(info.node, info.start)
    range.setEnd(info.node, end)

    const mention = document.createElement("span")
    mention.className = "inline-mention inline-mention--query"

    try {
      range.surroundContents(mention)
    } catch {
      const contents = range.extractContents()
      mention.appendChild(contents)
      range.insertNode(mention)
    }

    // Keep a single text node so continued typing stays in one place.
    const text = mention.textContent || "@"
    mention.replaceChildren(document.createTextNode(text))

    if (sel) {
      const caret = document.createRange()
      caret.setStart(mention.firstChild, mention.firstChild.textContent.length)
      caret.collapse(true)
      sel.removeAllRanges()
      sel.addRange(caret)
    }

    this.notifyInput()
    return { queryEl: mention, query: info.query }
  }

  // Turns pending query pills back into plain text (Escape, leaving the token).
  unwrapQueryMention() {
    if (!this.editor) return

    this.editor.querySelectorAll(this.constructor.QUERY_SELECTOR).forEach((el) => {
      const parent = el.parentNode
      if (!parent) return

      while (el.firstChild) parent.insertBefore(el.firstChild, el)
      parent.removeChild(el)
      parent.normalize()
    })
  }

  // --- Searching ---------------------------------------------------------------

  // Page-context rows (current record / company) offered without a search.
  get pageSuggestions() {
    return Array.isArray(this.pageSuggestionRows) ? this.pageSuggestionRows : []
  }

  // Resolves the page rows for wherever the user is now. Cached rows apply
  // immediately; otherwise the menu opens without them and repaints when the
  // fetch lands (if it is still open on the same page).
  syncPageSuggestions() {
    const page = this.headerRecordParams()
    if (!page) {
      this.pageSuggestionKey = null
      this.pageSuggestionRows = []
      return
    }

    const key = `${page.recordType}:${page.recordId}`
    this.pageSuggestionKey = key
    if (this.suggestionCache.has(key)) {
      this.pageSuggestionRows = this.suggestionCache.get(key)
      return
    }

    this.pageSuggestionRows = []
    this.fetchPageSuggestions(page, key)
  }

  async fetchPageSuggestions(page, key) {
    if (!this.hasSuggestionsUrlValue) return

    try {
      const params = new URLSearchParams({ record_type: page.recordType, record_id: page.recordId })
      const response = await fetch(`${this.suggestionsUrlValue}?${params}`, {
        headers: { "Accept": "application/json" },
      })
      if (!response.ok) return

      const rows = await response.json()
      if (!Array.isArray(rows)) return

      this.suggestionCache.set(key, rows)
      // Repaint only if the menu is still open on this page's rows — the user
      // may have closed it, or navigated and reopened it elsewhere.
      if (!this.menuOpen || this.pageSuggestionKey !== key) return

      this.pageSuggestionRows = rows
      this.search(this.atInfo?.query || "")
    } catch {
      // The menu simply offers no page rows; typed search still works.
    }
  }

  headerRecordParams() {
    const trigger = document.querySelector(this.constructor.HEADER_TRIGGER)
    if (!trigger?.href) return null

    const url = new URL(trigger.href, window.location.origin)
    const recordType = url.searchParams.get("record_type")
    const recordId = url.searchParams.get("record_id")
    if (!recordType || !recordId) return null

    return { recordType, recordId }
  }

  matchingSuggestions(query) {
    const q = query.trim().toLowerCase()
    if (!q) return this.pageSuggestions

    return this.pageSuggestions.filter((row) => {
      const name = (row.name || "").toLowerCase()
      const detail = (row.detail || "").toLowerCase()
      return name.includes(q) || detail.includes(q)
    })
  }

  search(query) {
    clearTimeout(this.debounceTimer)

    const pinned = this.matchingSuggestions(query)

    if (!query.trim()) {
      this.results = pinned
      if (pinned.length > 0) {
        this.renderResults()
      } else {
        this.renderHint("Type to search records…")
      }
      return
    }

    // Show matching page picks immediately; search results append after fetch.
    this.results = pinned
    if (pinned.length > 0) this.renderResults()
    this.debounceTimer = setTimeout(() => this.fetchResults(query, pinned), this.constructor.DEBOUNCE_MS)
  }

  async fetchResults(query, pinned = []) {
    if (this.fetchAbort) this.fetchAbort.abort()
    this.fetchAbort = new AbortController()

    try {
      const response = await fetch(`${this.urlValue}?q=${encodeURIComponent(query)}`, {
        headers: { "Accept": "application/json" },
        signal: this.fetchAbort.signal,
      })
      if (!response.ok) return

      const results = await response.json()
      // The menu may have closed (Escape, send) while the request was in flight.
      if (!this.menuOpen) return

      const seen = new Set(pinned.map((row) => `${row.record_type}:${row.record_id}`))
      const rest = results.filter((row) => !seen.has(`${row.record_type}:${row.record_id}`))
      this.results = [...pinned, ...rest]
      this.renderResults()
    } catch (error) {
      if (error.name !== "AbortError") throw error
    }
  }

  // --- Menu --------------------------------------------------------------------

  openMenu() {
    if (this.menuOpen || !this.hasMenuTarget) return

    this.menuOpen = true
    this.syncPageSuggestions()
    this.menuTarget.classList.remove("hidden")
    this.startFloating()
  }

  closeMenu({ unwrap = true } = {}) {
    clearTimeout(this.debounceTimer)
    if (this.fetchAbort) this.fetchAbort.abort()
    this.stopFloating()
    if (unwrap) this.unwrapQueryMention()
    this.atInfo = null
    this.results = []
    if (!this.menuOpen) {
      if (unwrap) this.notifyInput()
      return
    }

    this.menuOpen = false
    if (this.hasMenuTarget) this.menuTarget.classList.add("hidden")
    if (unwrap) this.notifyInput()
  }

  closeOnOutsideClick(event) {
    if (!this.menuOpen) return
    if (this.hasMenuTarget && this.menuTarget.contains(event.target)) return
    if (this.editor && this.editor.contains(event.target)) return
    this.closeMenu()
  }

  startFloating() {
    this.stopFloating()

    this.cleanupFloating = anchorMenu(this.editor, this.menuTarget)
  }

  stopFloating() {
    if (this.cleanupFloating) {
      this.cleanupFloating()
      this.cleanupFloating = null
    }
  }

  // --- Rendering ---------------------------------------------------------------

  renderHint(text) {
    if (!this.hasMenuTarget) return

    this.menuTarget.replaceChildren(this.buildHint(text))
  }

  renderResults() {
    if (!this.hasMenuTarget) return

    if (this.results.length === 0) {
      this.renderHint("No matching records")
      return
    }

    const rows = this.results.map((result, index) => this.buildOption(result, index))
    this.menuTarget.replaceChildren(...rows)
    this.setActive(0)
  }

  buildHint(text) {
    const hint = document.createElement("div")
    hint.className = "hudini__mention-empty"
    hint.textContent = text
    return hint
  }

  // Built with DOM APIs (not innerHTML) since names and details are user data.
  buildOption(result, index) {
    const option = document.createElement("div")
    option.className = "hudini__mention-option"
    option.setAttribute("role", "option")
    option.dataset.index = index

    option.appendChild(this.buildIcon(result, "hudini__mention-option-icon"))

    const body = document.createElement("span")
    body.className = "hudini__mention-option-body"

    const name = document.createElement("span")
    name.className = "hudini__mention-option-name"
    name.textContent = result.name
    body.appendChild(name)

    if (result.detail) {
      const detail = document.createElement("span")
      detail.className = "hudini__mention-option-detail"
      detail.textContent = result.detail
      body.appendChild(detail)
    }

    option.appendChild(body)

    // mousedown (not click) so the contenteditable doesn't blur before the pick.
    option.addEventListener("mousedown", (event) => {
      event.preventDefault()
      this.selectResult(result)
    })
    option.addEventListener("mouseenter", () => this.setActive(index))

    return option
  }

  buildIcon(result, className) {
    const iconWrap = document.createElement("span")
    iconWrap.className = className
    // Match InlineMentionComponent / `.asset-icon`: layout colors live on the
    // container so the FA glyph inherits `color`.
    if (result.icon_background) iconWrap.style.backgroundColor = result.icon_background
    if (result.icon_color) iconWrap.style.color = result.icon_color

    const icon = document.createElement("i")
    icon.className = result.icon
    iconWrap.appendChild(icon)

    return iconWrap
  }

  // --- Keyboard navigation -----------------------------------------------------

  // Stops propagation so the composer's own Enter/Escape handling (send / stop)
  // doesn't also fire while the menu is open.
  handleKeydown(event) {
    if (!this.menuOpen) return

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault(); event.stopImmediatePropagation(); this.moveActive(1); break
      case "ArrowUp":
        event.preventDefault(); event.stopImmediatePropagation(); this.moveActive(-1); break
      case "Enter":
      case "Tab":
        if (this.results.length === 0) { this.closeMenu(); return }
        event.preventDefault(); event.stopImmediatePropagation(); this.selectActive(); break
      case "Escape":
        event.preventDefault(); event.stopImmediatePropagation(); this.closeMenu(); break
    }
  }

  get optionElements() {
    return this.hasMenuTarget ? Array.from(this.menuTarget.querySelectorAll(".hudini__mention-option")) : []
  }

  setActive(index) {
    const options = this.optionElements
    if (options.length === 0) return

    this.activeIndex = Math.max(0, Math.min(index, options.length - 1))
    options.forEach((option, i) => {
      option.classList.toggle("hudini__mention-option--active", i === this.activeIndex)
    })
    options[this.activeIndex].scrollIntoView({ block: "nearest" })
  }

  moveActive(delta) {
    this.setActive((this.activeIndex || 0) + delta)
  }

  selectActive() {
    const result = this.results[this.activeIndex || 0]
    if (result) this.selectResult(result)
  }

  // --- Inserting the mention -----------------------------------------------------

  // Replaces the pending `@query` pill (or plain-text `@query`) with an inline,
  // non-editable mention and puts the caret after it (with a trailing space so
  // typing can continue).
  selectResult(result) {
    const editor = this.editor
    const info = this.atInfo
    const queryEl = info?.queryEl
    this.closeMenu({ unwrap: false })
    if (!editor) return

    editor.focus()
    const sel = window.getSelection()
    const mention = this.buildMention(result)

    if (queryEl && editor.contains(queryEl)) {
      queryEl.replaceWith(mention)
    } else if (info?.node && editor.contains(info.node)) {
      const { node, start } = info
      const caret = (sel.rangeCount && sel.getRangeAt(0).endContainer === node)
        ? sel.getRangeAt(0).endOffset
        : node.textContent.length
      node.textContent = node.textContent.slice(0, start) + node.textContent.slice(caret)

      const range = document.createRange()
      range.setStart(node, start)
      range.collapse(true)
      range.insertNode(mention)
    } else {
      return
    }

    const space = document.createTextNode("\u00A0")
    const range = document.createRange()
    range.setStartAfter(mention)
    range.collapse(true)
    range.insertNode(space)
    range.setStartAfter(space)
    range.collapse(true)
    sel.removeAllRanges()
    sel.addRange(range)

    this.notifyInput()
  }

  // Opens the record a composer mention points at. The mention is an island of
  // `contenteditable="false"` inside the editor, where a real link is not
  // followed — the browser selects the token to edit it instead — so the
  // navigation has to be explicit. A new tab, like the mentions on a sent turn:
  // the panel lives in a turbo frame, and the half-written message stays put.
  openRecord(event) {
    const mention = event.target.closest("[data-record-url]")
    if (!mention || !this.editor?.contains(mention)) return

    event.preventDefault()
    window.open(mention.dataset.recordUrl, "_blank", "noopener")
  }

  // Matches InlineMentionComponent markup so composer mentions share the
  // design-system inline-mention styles.
  buildMention(result) {
    const mention = document.createElement("span")
    mention.className = "inline-mention"
    mention.contentEditable = "false"
    mention.dataset.recordType = result.record_type
    mention.dataset.recordId = result.record_id
    mention.dataset.recordName = result.name
    if (result.url) mention.dataset.recordUrl = result.url

    mention.appendChild(this.buildIcon(result, "inline-mention__icon"))

    const text = document.createElement("span")
    text.className = "inline-mention__text"
    text.textContent = result.name
    mention.appendChild(text)

    return mention
  }

  // Notifies the host so it can refresh its empty/placeholder state after the
  // mention is inserted programmatically.
  notifyInput() {
    if (this.editor) this.editor.dispatchEvent(new Event("input", { bubbles: true }))
  }
}
